/**
 * waypoints.ts — shared client helpers for personal map markers.
 *
 * PLANET-AWARE: `myWaypoints` returns only the rows stamped with the world
 * the player currently stands in — a marker set on another planet never
 * reaches a map here. Creation goes through `createWaypoint`, whose world
 * is the SERVER's view of where you are (never a client argument).
 */
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';

export interface WaypointRow {
  waypointId: bigint;
  owner: { isEqual(o: unknown): boolean };
  worldId: bigint;
  name: string;
  posX: number;
  posZ: number;
}

export const WAYPOINT_COLOR = '#ffd166';

/** The player's own waypoints ON THE CURRENT WORLD (the planet-aware filter). */
export function myWaypoints(conn: DbConnection | null): WaypointRow[] {
  const out: WaypointRow[] = [];
  if (!conn?.identity || atlas.currentWorldId === null) return out;
  try {
    for (const w of conn.db.waypoint.iter()) {
      if (w.worldId === atlas.currentWorldId && w.owner.isEqual(conn.identity)) out.push(w as unknown as WaypointRow);
    }
  } catch { /* row cache mid-churn */ }
  return out;
}

/** Create at (wx, wz): prompts for a name; empty/cancel aborts. */
export function promptCreateWaypoint(conn: DbConnection, wx: number, wz: number): void {
  const name = window.prompt(`Name this waypoint (${Math.round(wx)}, ${Math.round(wz)})`, 'Waypoint');
  if (name === null || name.trim() === '') return;
  conn.reducers.createWaypoint({ name: name.trim().slice(0, 32), posX: wx, posZ: wz })
    .then(() => bus.emit('atlas:status', { text: `Waypoint “${name.trim().slice(0, 32)}” set` }))
    .catch((e: Error) => bus.emit('atlas:status', { text: `Waypoint refused: ${e.message}` }));
}

/** Edit an existing marker: rename, or clear the text to delete it. */
export function promptEditWaypoint(conn: DbConnection, w: WaypointRow): void {
  const next = window.prompt(`Rename “${w.name}” — or CLEAR the text to delete it`, w.name);
  if (next === null) return;
  if (next.trim() === '') {
    conn.reducers.deleteWaypoint({ waypointId: w.waypointId })
      .then(() => bus.emit('atlas:status', { text: `Waypoint “${w.name}” deleted` }))
      .catch((e: Error) => bus.emit('atlas:status', { text: `Delete refused: ${e.message}` }));
    return;
  }
  if (next.trim() !== w.name) {
    conn.reducers.renameWaypoint({ waypointId: w.waypointId, name: next.trim().slice(0, 32) })
      .catch((e: Error) => bus.emit('atlas:status', { text: `Rename refused: ${e.message}` }));
  }
}

/** Draw the shared marker glyph: a gold diamond + name tag. */
export function drawWaypointMarker(g: CanvasRenderingContext2D, x: number, y: number, name: string, dim = false): void {
  g.save();
  g.globalAlpha = dim ? 0.55 : 1;
  g.fillStyle = WAYPOINT_COLOR;
  g.strokeStyle = '#0a0f18';
  g.lineWidth = 1;
  g.beginPath();
  g.moveTo(x, y - 6); g.lineTo(x + 5, y); g.lineTo(x, y + 6); g.lineTo(x - 5, y);
  g.closePath();
  g.fill();
  g.stroke();
  if (name) {
    g.font = '10px system-ui';
    const tw = g.measureText(name).width;
    g.fillStyle = 'rgba(8,12,20,.75)';
    g.fillRect(x + 7, y - 7, tw + 6, 14);
    g.fillStyle = WAYPOINT_COLOR;
    g.fillText(name, x + 10, y + 4);
  }
  g.restore();
}
